import React from "react";
import { Sparkles, Target, Compass, Linkedin, Mail, Github } from "lucide-react";

const PILLARS = [
  {
    icon: Target,
    title: "Clear insights, not black boxes",
    description:
      "Structured, honest information on every job opening — skills required, recruiter details, and real application outcomes.",
  },
  {
    icon: Compass,
    title: "Apply smarter, not harder",
    description:
      "From a saved outreach template to AI mock interviews, every tool here exists to cut down the hours you spend guessing.",
  },
  {
    icon: Sparkles,
    title: "Candidate-first, always",
    description:
      "No vague JD buzzwords or misleading posts — we're building toward a hiring process that respects your time.",
  },
];

const SOCIALS = [
  { label: "LinkedIn", icon: Linkedin, href: "https://www.linkedin.com/in/rohitshekharsingh/" },
  { label: "Email", icon: Mail, href: "mailto:rohitshekrsingh@gmail.com" },
  { label: "GitHub", icon: Github, href: "https://github.com/rohitshekhar" },
];

const About = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Header */}
    <div className="bg-gradient-to-b from-blue-50 to-white border-b border-gray-100">
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <span className="inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
          <Sparkles className="w-3.5 h-3.5" />
          About RealHired
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Bringing transparency and clarity to the job hunt
        </h1>
        <p className="text-lg text-gray-600">
          We're building a platform that cuts through the noise of job portals by surfacing
          the real picture — no more black boxes, vague JD buzzwords, or misleading posts.
        </p>
      </div>
    </div>

    <div className="max-w-3xl mx-auto px-6 py-14 space-y-12">
      {/* Mission */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Our mission</h2>
        <div className="space-y-4 text-gray-600 leading-relaxed">
          <p>
            Our mission is to simplify job discovery for candidates who are tired of spending
            hours applying to roles without feedback or understanding. We give you clear,
            structured insights into job openings, skill requirements, recruiter details, and
            application outcomes — all in one place.
          </p>
          <p>
            Whether you're just starting your career or actively switching jobs, our goal is to
            make the journey less overwhelming and more transparent. Think of us as your
            personal job-hunting assistant — helping you apply smarter, not harder.
          </p>
          <p>
            We're not just another job board. We're a movement towards honesty, clarity, and
            candidate-first hiring.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-5">What we stand for</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {PILLARS.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <div
                key={pillar.title}
                className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-blue-200 hover:shadow-lg transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 mb-4">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1.5">{pillar.title}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{pillar.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Founder */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-5">Founder</h2>
        <div className="flex items-center gap-4 mb-2">
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-lg font-bold shrink-0">
            RS
          </span>
          <div>
            <p className="text-base font-semibold text-gray-900">Rohit Shekhar Singh</p>
            <p className="text-sm text-blue-600 font-medium">Founder & CEO</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6">
          {SOCIALS.map((social) => {
            const Icon = social.icon;
            return (
              <a
                key={social.label}
                href={social.href}
                target={social.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-colors"
              >
                <Icon size={15} />
                {social.label}
              </a>
            );
          })}
        </div>
      </section>
    </div>
  </div>
);

export default About;
